import { useState } from 'react';
import { useOutletContext } from 'react-router';
import { Banner, useApiClient, useQuery } from '@acc/web-shared';
import { PageHead, dateTime, useAgency } from '../agency';

interface Item {
  kind: 'intake' | 'opportunity';
  id: string;
  customerName: string;
  type: string;
  archivedAt: string;
  archivedBy: string;
  pcStatus?: string | null;
  lifeStatus?: string | null;
  ownerMembershipId?: string | null;
}

/** Archived quote requests and quotes, for the owner and office manager to restore (baseline 9.9). */
export function Archive() {
  const a = useAgency();
  const api = useApiClient();
  const { refreshCounts } = useOutletContext<{ refreshCounts: () => void }>();
  const q = useQuery<{ items: Item[] }>(a.manager ? a.path('/archive') : null);
  const [busy, setBusy] = useState('');

  async function restore(x: Item) {
    setBusy(x.id);
    try {
      await api(a.path(`/archive/${x.id}/restore`), { method: 'POST', body: { kind: x.kind } });
      a.notify('ok', `${x.customerName} restored.`);
      q.reload(); refreshCounts();
    } catch (err) { a.notify('bad', (err as Error).message); } finally { setBusy(''); }
  }

  if (!a.manager) return <Banner tone="warn">The archive is for the agency owner and office manager.</Banner>;
  const items = q.data?.items ?? [];
  return (
    <>
      <PageHead title="Archive" sub="Archived quote requests and quotes, kept with their history. Restoring puts them back exactly as they were." />
      <div className="card">
        {q.error && <Banner tone="bad">{q.error.message}</Banner>}
        {q.loading && !q.data && <p className="muted">Loading…</p>}
        {q.data && !items.length && <div className="empty">Nothing archived.</div>}
        {items.length > 0 && (
          <table className="data">
            <thead><tr><th>Customer</th><th>What</th><th>Archived</th><th>By</th><th /></tr></thead>
            <tbody>
              {items.map((x) => (
                <tr key={`${x.kind}:${x.id}`}>
                  <td><strong>{x.customerName}</strong></td>
                  <td>{x.kind === 'intake' ? 'Quote request' : `Quote · ${[x.pcStatus && `P/C ${x.pcStatus}`, x.lifeStatus && `Life ${x.lifeStatus}`].filter(Boolean).join(' · ') || x.type} · ${a.name(x.ownerMembershipId)}`}</td>
                  <td className="muted">{dateTime(x.archivedAt)}</td>
                  <td className="muted">{a.name(x.archivedBy)}</td>
                  <td style={{ textAlign: 'right' }}><button type="button" className="btn small" disabled={busy === x.id} onClick={() => void restore(x)} aria-label={`Restore ${x.customerName}`}>Restore</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
